import { FC, PropsWithChildren } from 'react'

import { AuthContextProvider, AuthContextType, authContextRef } from '~contexts'
import { useCallback, useEffect, useMemo, useState } from '~hooks'
import { useSignIn, useGetUser } from '~query-hooks'
import {
  clearStorageCacheQueries,
  deleteRefreshToken,
  deleteToken,
  getToken,
  setRefreshToken,
  setToken,
  signInRequestOtp,
} from '~services'
import { secureStore } from '~utils'
import { ASYNC_STORAGE_KEYS } from '~constants'
import { SignUpFormValues } from '~types/authForms'
import { useResetCache, wait } from '~utils'

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null)
  const [profileUpdated, setProfileUpdated] = useState<boolean>(false)
  const { signInUser } = useSignIn()
  const { user, refetchUser } = useGetUser({ enabled: !!isSignedIn })
  const resetCache = useResetCache()

  useEffect(() => {
    setProfileUpdated(!!user?.basicInfo?.profileUpdated)
  }, [user?.basicInfo?.profileUpdated])

  useEffect(() => {
    const bootstrap = async () => {
      // TODO: This should be moved to backend calls, in this bootstrap function we should fetch user info and not token
      const token = await getToken()
      setIsSignedIn(!!token)
      if (token) {
        await refetchUser()
      }
    }
    bootstrap()
  }, [refetchUser])

  const signIn: AuthContextType['signIn'] = useCallback(
    async ({ email, magicToken, enrollmentSource }) => {
      // Errors are handled on UI side
      // if you want to stop this function with error just throw new Error.
      // Remember to pass readable error message for user, because this error will be displayed for him

      const { access, refresh } = await signInUser({ email, magicToken, enrollmentSource })
      if (access) {
        await setToken(access)
        setIsSignedIn(true)
        await refetchUser()
      }
      refresh && (await setRefreshToken(refresh))
    },
    [refetchUser, signInUser]
  )
  // FIXME: remove this logic when BE deliver login to apple review and to history
  const signInWithOtp: AuthContextType['signInWithOtp'] = useCallback(
    async ({ email }) => {
      const { access, refresh } = await signInRequestOtp({ email })
      if (access) {
        await setToken(access)
        setIsSignedIn(true)
        await refetchUser()
      }
      refresh && (await setRefreshToken(refresh))
    },
    [refetchUser]
  )

  const signOut = useCallback(async () => {
    setIsSignedIn(false)
    await clearStorageCacheQueries()
    resetCache()
    await deleteToken()
    await deleteRefreshToken()
    // Clear the rewards popup flag so it shows again on next login if profile is incomplete
    await secureStore.removeItem(ASYNC_STORAGE_KEYS.REWARDS_POPUP_SHOWN)
  }, [resetCache])

  const signUp = useCallback(async (data: SignUpFormValues) => {
    // temporary solution
    await wait(500)
    setIsSignedIn(true)
  }, [])

  const value = useMemo(() => {
    return {
      isSignedIn,
      profileUpdated,
      signIn,
      signInWithOtp,
      signOut,
      signUp,
    }
  }, [isSignedIn, profileUpdated, signIn, signInWithOtp, signOut, signUp])

  // @ts-expect-error: wrong interface of react createRef()
  authContextRef.current = value
  return <AuthContextProvider value={value}>{children}</AuthContextProvider>
}
